import { useQuery } from '@apollo/client';
import { DataCollectionBuildingGetBuildingQuery } from '@predium/client-graphql';
import { useSnackbar } from 'notistack';
import { createContext, PropsWithChildren } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import LoadingScreen from '../../../../components/LoadingScreen';
import { createStandardErrorSnackbar } from '../../../../components/NotistackProvider';
import { GET_BUILDING } from '../../../../graphql/DataCollection.queries';
import { PATHS } from '../../../../routes';

type BuildingContextProps = {
  building: NonNullable<DataCollectionBuildingGetBuildingQuery['building_by_pk']>;
  hasEditAccess: boolean;
  refetchBuilding: VoidFunction;
};

const BuildingContext = createContext<BuildingContextProps | null>(null);

function BuildingContextProvider({ children }: PropsWithChildren) {
  const { enqueueSnackbar } = useSnackbar();
  const { id: buildingIdString } = useParams();
  const buildingId = parseInt(buildingIdString!);

  const {
    data,
    previousData,
    refetch: refetchBuilding,
  } = useQuery(GET_BUILDING, {
    variables: { buildingId },
    fetchPolicy: 'cache-and-network',
    onError: () => createStandardErrorSnackbar()(enqueueSnackbar),
  });

  const currentData = data ?? previousData;

  if (!currentData) return <LoadingScreen />;

  if (!currentData.building_by_pk) {
    return <Navigate to={PATHS.notFound()} />;
  }

  const hasEditAccess = currentData.building_by_pk.has_write_permission;

  return (
    <BuildingContext.Provider
      value={{
        building: currentData.building_by_pk,
        hasEditAccess,
        refetchBuilding,
      }}
    >
      {children}
    </BuildingContext.Provider>
  );
}

export { BuildingContext, BuildingContextProvider };
